/**
파일명: contactFormTransformers.js
설명: 문의 폼/목록 도메인에서 사용하는 문자열 정규화와 API 응답 매핑 함수를 제공합니다.
*/

import { STATUS_COMPLETED, STATUS_CONFIRMED, STATUS_REVIEWING } from '../constants/contactFormConstants';

/**
// [비즈니스] 리치 텍스트 입력값을 서버 저장 가능한 일반 텍스트로 정규화합니다.
// [기능] HTML 문자열에서 줄바꿈/태그/공백 엔티티를 처리해 평문 문자열을 반환합니다.
// [기술] 정규식을 순차 적용해 브라우저 의존 없이 클라이언트 단에서 일관된 텍스트를 생성합니다.
*/
export function getPlainMessage(htmlMessage) {
  return htmlMessage
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

/**
// [비즈니스] 검증 결과에서 에러 존재 여부를 빠르게 판단해 제출 가능 상태를 결정합니다.
// [기능] 에러 맵의 값 중 하나라도 truthy면 true를 반환합니다.
// [기술] Object.values + some 조합으로 키 개수와 무관한 단순 탐색을 수행합니다.
*/
export function hasAnyError(errorMap) {
  return Object.values(errorMap).some((message) => Boolean(message));
}

/**
// [비즈니스] 문의 등록일을 UI 표기 형식(yyyy.mm.dd)으로 통일해 가독성을 높입니다.
// [기능] ISO/날짜 문자열을 받아 유효하면 yyyy.mm.dd 문자열을 반환하고, 아니면 빈 문자열을 반환합니다.
// [기술] Date 파싱 실패(NaN)를 방어한 뒤 toISOString 결과를 잘라 포맷을 고정합니다.
*/
export function formatCreateDate(createDate) {
  if (!createDate) return '';
  const parsed = new Date(createDate);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10).replace(/-/g, '.');
}

/**
// [비즈니스] 문의 상태값이 예외 입력이어도 화면에서 의미 있는 상태로 표시되게 보정합니다.
// [기능] 허용 상태(답변완료/확인 완료/검토중)를 우선 반환하고, 그 외 문자열은 trim 후 반환합니다.
// [기술] 불명확한 상태값은 기본값 검토중으로 fallback 처리해 UI 분기 안정성을 보장합니다.
*/
export function normalizeStatus(status) {
  if (status === STATUS_COMPLETED || status === STATUS_CONFIRMED || status === STATUS_REVIEWING) {
    return status;
  }
  if (typeof status === 'string' && status.trim()) {
    return status.trim();
  }
  return STATUS_REVIEWING;
}

/**
// [비즈니스] 삭제 불가 상태를 클라이언트에서 즉시 판단해 불필요한 API 호출을 줄입니다.
// [기능] 상태값이 답변완료 또는 확인 완료면 true를 반환합니다.
// [기술] 상태 상수 비교만 수행하는 순수 함수로, UI/훅 어디서든 재사용 가능합니다.
*/
export function isCompletedStatus(status) {
  return status === STATUS_COMPLETED || status === STATUS_CONFIRMED;
}

/**
// [비즈니스] 목록 API 응답 스키마 차이(camel/snake)를 흡수해 화면 렌더링을 안정화합니다.
// [기능] 목록 아이템 payload 1건을 화면용 목록 모델로 변환해 반환합니다.
// [기술] null 병합 연산자로 복수 키를 순차 대응하고 날짜/상태 정규화 함수를 합성합니다.
*/
export function mapListItem(item) {
  return {
    id: item.id,
    inquiryType: item.inquiryType ?? item.inquiry_type ?? '',
    title: item.title ?? '',
    managerName: item.managerName ?? '',
    date: formatCreateDate(item.createDate),
    status: normalizeStatus(item.status),
  };
}

/**
// [비즈니스] 상세 API 응답의 누락값/스키마 차이를 보정해 문의 열람 화면 품질을 유지합니다.
// [기능] 상세 payload를 화면 표시용 상세 모델로 변환합니다.
// [기술] 문자열 필드는 trim 후 빈값이면 "-"로 치환하고, 상태/날짜는 전용 정규화 함수를 사용합니다.
*/
export function mapInquiryDetail(payload) {
  const normalizeValue = (value) => {
    if (typeof value !== 'string') {
      return value == null ? '-' : String(value);
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : '-';
  };

  return {
    id: payload.id,
    inquiryType: normalizeValue(payload.inquiryType ?? payload.inquiry_type),
    title: normalizeValue(payload.title),
    company: normalizeValue(payload.company ?? payload.company_name),
    managerName: normalizeValue(payload.managerName ?? payload.manager_name),
    phone: normalizeValue(payload.phone),
    email: normalizeValue(payload.email ?? payload.email_address),
    content: normalizeValue(payload.content),
    status: normalizeStatus(payload.status),
    createDate: formatCreateDate(payload.createDate),
  };
}
